import {
  API_CENTRIFUGE_REPORT_DEFAULTS_ENDPOINT,
  API_CENTRIFUGE_REPORTS_ENDPOINT,
} from '@/api/config';
import apiClient from '@/api/client';
import { LocalizedNumberInput } from '@/components/LocalizedNumberInput/LocalizedNumberInput';
import { useLanguage } from '@/i18n/LanguageContext';
import type {
  CentrifugeReport,
  CentrifugeReportDefaults,
  CentrifugeMode,
  CentrifugeRole,
  InferenceResult,
} from '@/models/models';
import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

type NumericValue = number | string;

type OperatingValues = {
  bowl_speed_rpm: NumericValue;
  backdrive_differential_rpm: NumericValue;
  bowl_diameter_inches: NumericValue;
};

interface Props {
  opened: boolean;
  selection: Partial<Record<CentrifugeRole, InferenceResult>>;
  onClose: () => void;
  onCreated: (report: CentrifugeReport) => void;
  allowReuse?: boolean;
}

const ROLE_ORDER: CentrifugeRole[] = [
  'ls_suction',
  'ls_effluent',
  'drying_effluent',
  'hs_effluent',
  'centrifuge_1_suction',
  'centrifuge_1_effluent',
  'centrifuge_2_suction',
  'centrifuge_2_effluent',
];

const DUAL_ROLES = new Set<CentrifugeRole>([
  'centrifuge_1_suction',
  'centrifuge_1_effluent',
  'centrifuge_2_suction',
  'centrifuge_2_effluent',
]);

const HIGH_SPEED_DEFAULTS = {
  bowl_speed_rpm: 2850,
  backdrive_differential_rpm: 75,
  bowl_diameter_inches: 20,
};

const STANDARD_DEFAULTS: CentrifugeReportDefaults = {
  source: 'standard',
  source_report_id: null,
  mode: 'barite_recovery',
  flow_rates_gpm: {
    ls_suction: 30,
    ls_effluent: 15,
    drying_effluent: 5,
    hs_effluent: 14,
    centrifuge_1_suction: 30,
    centrifuge_1_effluent: 24,
    centrifuge_2_suction: 30,
    centrifuge_2_effluent: 24,
  },
  low_speed: {
    bowl_speed_rpm: 600,
    backdrive_differential_rpm: 50,
    bowl_diameter_inches: 20,
  },
  high_speed: HIGH_SPEED_DEFAULTS,
  centrifuge_1: HIGH_SPEED_DEFAULTS,
  centrifuge_2: HIGH_SPEED_DEFAULTS,
};

const operatingValues = (
  values: CentrifugeReportDefaults['low_speed'] | undefined,
  fallback = HIGH_SPEED_DEFAULTS
): OperatingValues => ({ ...(values || fallback) });

function numberValue(value: NumericValue): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }
  return fallback;
}

export default function GenerateCentrifugeReportModal({
  opened,
  selection,
  onClose,
  onCreated,
  allowReuse = false,
}: Props) {
  const { t } = useLanguage();
  const selectedRoles = useMemo(
    () => ROLE_ORDER.filter((role) => selection[role]),
    [selection]
  );
  const mode: CentrifugeMode = selectedRoles.some((role) => DUAL_ROLES.has(role))
    ? 'dual_polishing'
    : 'barite_recovery';
  const isDual = mode === 'dual_polishing';
  const [flowRates, setFlowRates] = useState<Partial<Record<CentrifugeRole, NumericValue>>>({});
  const selectionKey = selectedRoles
    .map((role) => `${role}:${selection[role]!.id}`)
    .join('|');
  const [lowSpeed, setLowSpeed] = useState<OperatingValues>(
    operatingValues(STANDARD_DEFAULTS.low_speed)
  );
  const [highSpeed, setHighSpeed] = useState<OperatingValues>(
    operatingValues(STANDARD_DEFAULTS.high_speed)
  );
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [defaultsSource, setDefaultsSource] = useState<{
    source: CentrifugeReportDefaults['source'];
    reportId: string | null;
  }>({ source: 'standard', reportId: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    let active = true;
    const applyDefaults = (defaults: CentrifugeReportDefaults) => {
      const nextFlows = Object.fromEntries(
        selectedRoles.map((role) => [role, defaults.flow_rates_gpm[role]])
      ) as Partial<Record<CentrifugeRole, NumericValue>>;
      if (!isDual) {
        const lsEffluent = numberValue(nextFlows.ls_effluent ?? '') ?? 0;
        const dryingEffluent = selection.drying_effluent
          ? numberValue(nextFlows.drying_effluent ?? '') ?? 0
          : 0;
        const hsFeed = lsEffluent + dryingEffluent;
        const hsEffluent = numberValue(nextFlows.hs_effluent ?? '') ?? 0;
        if (hsFeed > 0 && hsEffluent >= hsFeed) {
          nextFlows.hs_effluent = Math.max(0.001, Number((hsFeed * 0.9).toFixed(3)));
        }
      }

      setFlowRates(nextFlows);
      setLowSpeed(operatingValues(
        isDual ? defaults.centrifuge_1 : defaults.low_speed,
        isDual ? STANDARD_DEFAULTS.centrifuge_1 : STANDARD_DEFAULTS.low_speed
      ));
      setHighSpeed(operatingValues(
        isDual ? defaults.centrifuge_2 : defaults.high_speed,
        isDual ? STANDARD_DEFAULTS.centrifuge_2 : STANDARD_DEFAULTS.high_speed
      ));
      setDefaultsSource({
        source: defaults.source,
        reportId: defaults.source_report_id,
      });
    };

    applyDefaults({
      ...STANDARD_DEFAULTS,
      mode,
      flow_rates_gpm: {
        ...STANDARD_DEFAULTS.flow_rates_gpm,
        hs_effluent: selection.drying_effluent ? 14 : 10.5,
      },
    });
    setError(null);
    setDefaultsLoading(true);

    const referenceResultId = selection.ls_suction?.id ?? selection[selectedRoles[0]]?.id;
    if (!referenceResultId) {
      setDefaultsLoading(false);
      return () => { active = false; };
    }

    void apiClient.get<CentrifugeReportDefaults>(
      API_CENTRIFUGE_REPORT_DEFAULTS_ENDPOINT,
      {
        params: {
          inference_result_id: referenceResultId,
          drying_effluent_included: Boolean(selection.drying_effluent),
          mode,
        },
      }
    ).then((response) => {
      if (active) applyDefaults(response.data);
    }).catch((requestError) => {
      console.error('Failed to load centrifuge report defaults:', requestError);
    }).finally(() => {
      if (active) setDefaultsLoading(false);
    });

    return () => { active = false; };
  }, [opened, selectedRoles, selection, selectionKey, isDual, mode]);

  const operatingValid = (values: OperatingValues) => {
    const bowlSpeed = numberValue(values.bowl_speed_rpm);
    const backdrive = numberValue(values.backdrive_differential_rpm);
    const diameter = numberValue(values.bowl_diameter_inches);
    return (
      bowlSpeed !== null && bowlSpeed > 0 && bowlSpeed <= 10000 &&
      backdrive !== null && backdrive >= 0 && backdrive <= 1000 &&
      diameter !== null && diameter >= 1 && diameter <= 100
    );
  };

  const flowErrors = useMemo(() => {
    const errors: Partial<Record<CentrifugeRole, string>> = {};
    for (const role of selectedRoles) {
      const value = numberValue(flowRates[role] ?? '');
      if (value === null || value <= 0 || value > 1000) {
        errors[role] = t('centrifuge.flowValueError');
      }
    }

    if (isDual) {
      for (const number of [1, 2] as const) {
        const suctionRole = `centrifuge_${number}_suction` as CentrifugeRole;
        const effluentRole = `centrifuge_${number}_effluent` as CentrifugeRole;
        const suction = numberValue(flowRates[suctionRole] ?? '');
        const effluent = numberValue(flowRates[effluentRole] ?? '');
        if (
          !errors[suctionRole] && !errors[effluentRole] &&
          suction !== null && effluent !== null && suction <= effluent
        ) {
          errors[suctionRole] = t('centrifuge.dualFlowOrderError', { number });
        }
      }
    } else {
      const lsSuction = numberValue(flowRates.ls_suction ?? '');
      const lsEffluent = numberValue(flowRates.ls_effluent ?? '');
      if (
        !errors.ls_suction && !errors.ls_effluent &&
        lsSuction !== null && lsEffluent !== null &&
        lsSuction <= lsEffluent
      ) {
        errors.ls_suction = t('centrifuge.lsFlowOrderError');
      }

      const hsEffluent = numberValue(flowRates.hs_effluent ?? '');
      const dryingEffluent = selection.drying_effluent
        ? numberValue(flowRates.drying_effluent ?? '')
        : 0;
      if (
        !errors.ls_effluent && !errors.hs_effluent &&
        (!selection.drying_effluent || !errors.drying_effluent) &&
        lsEffluent !== null && hsEffluent !== null && dryingEffluent !== null &&
        hsEffluent >= lsEffluent + dryingEffluent
      ) {
        errors.hs_effluent = selection.drying_effluent
          ? t('centrifuge.hsFlowOrderWithDryingError')
          : t('centrifuge.hsFlowOrderError');
      }
    }
    return errors;
  }, [flowRates, selectedRoles, selection.drying_effluent, isDual, t]);

  const formValid = (
    selectedRoles.every((role) => !flowErrors[role]) &&
    operatingValid(lowSpeed) &&
    operatingValid(highSpeed) &&
    !defaultsLoading
  );

  const submit = async () => {
    if (!formValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const measurementPayload = Object.fromEntries(
        selectedRoles.map((role) => [
          role,
          {
            inference_result_id: selection[role]!.id,
            flow_rate_gpm: numberValue(flowRates[role]!)!,
          },
        ])
      );
      const response = await apiClient.post<CentrifugeReport>(
        API_CENTRIFUGE_REPORTS_ENDPOINT,
        {
          mode,
          ...measurementPayload,
          [isDual ? 'centrifuge_1' : 'low_speed']: {
            bowl_speed_rpm: numberValue(lowSpeed.bowl_speed_rpm),
            backdrive_differential_rpm: numberValue(lowSpeed.backdrive_differential_rpm),
            bowl_diameter_inches: numberValue(lowSpeed.bowl_diameter_inches),
          },
          [isDual ? 'centrifuge_2' : 'high_speed']: {
            bowl_speed_rpm: numberValue(highSpeed.bowl_speed_rpm),
            backdrive_differential_rpm: numberValue(highSpeed.backdrive_differential_rpm),
            bowl_diameter_inches: numberValue(highSpeed.bowl_diameter_inches),
          },
          allow_reuse: allowReuse,
        }
      );
      onCreated(response.data);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, t('centrifuge.createError')));
    } finally {
      setSubmitting(false);
    }
  };

  const rangeError = (
    value: NumericValue,
    minimum: number,
    maximum: number,
    minimumInclusive = true
  ) => {
    const parsed = numberValue(value);
    const aboveMinimum = parsed !== null && (
      minimumInclusive ? parsed >= minimum : parsed > minimum
    );
    return parsed !== null && aboveMinimum && parsed <= maximum
      ? undefined
      : t('centrifuge.valueRangeError');
  };

  const operatingInputs = (
    values: OperatingValues,
    setValues: (values: OperatingValues) => void
  ) => (
    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
      <LocalizedNumberInput
        label={t('centrifuge.bowlSpeed')}
        description={t('centrifuge.bowlSpeedRange')}
        min={1}
        max={10000}
        value={values.bowl_speed_rpm}
        onChange={(value) => setValues({ ...values, bowl_speed_rpm: value })}
        suffix=" rpm"
        error={rangeError(values.bowl_speed_rpm, 0, 10000, false)}
        disabled={submitting || defaultsLoading}
        required
      />
      <LocalizedNumberInput
        label={t('centrifuge.backdrive')}
        description={t('centrifuge.backdriveRange')}
        min={0}
        max={1000}
        value={values.backdrive_differential_rpm}
        onChange={(value) => setValues({ ...values, backdrive_differential_rpm: value })}
        suffix=" rpm"
        error={rangeError(values.backdrive_differential_rpm, 0, 1000)}
        disabled={submitting || defaultsLoading}
        required
      />
      <LocalizedNumberInput
        label={t('centrifuge.bowlDiameter')}
        description={t('centrifuge.bowlDiameterRange')}
        min={1}
        max={100}
        value={values.bowl_diameter_inches}
        onChange={(value) => setValues({ ...values, bowl_diameter_inches: value })}
        suffix=" in"
        error={rangeError(values.bowl_diameter_inches, 1, 100)}
        disabled={submitting || defaultsLoading}
        required
      />
    </SimpleGrid>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('centrifuge.generateTitle')}
      centered
      size="xl"
      closeOnClickOutside={!submitting}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t(isDual
            ? 'centrifuge.generateDescriptionDual'
            : 'centrifuge.generateDescription')}
        </Text>
        <Text size="sm" c="dimmed">
          {defaultsLoading
            ? t('centrifuge.loadingDefaults')
            : defaultsSource.source === 'previous_report' && defaultsSource.reportId
              ? t('centrifuge.previousReportDefaults', {
                  report: defaultsSource.reportId,
                })
              : t('centrifuge.standardDefaults')}
        </Text>
        {error && <Alert color="red">{error}</Alert>}

        <Paper withBorder p="md" radius="md">
          <Text fw={700} mb="sm">{t('centrifuge.streamFlows')}</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {selectedRoles.map((role) => (
              <LocalizedNumberInput
                key={role}
                label={t(`centrifuge.role.${role}`)}
                description={`${t('centrifuge.result')} #${selection[role]!.id} · ${t('centrifuge.flowRange')}`}
                min={0.001}
                max={1000}
                decimalScale={3}
                value={flowRates[role] ?? ''}
                onChange={(value) => setFlowRates((current) => ({ ...current, [role]: value }))}
                suffix=" gpm"
                error={flowErrors[role]}
                disabled={submitting || defaultsLoading}
                required
              />
            ))}
          </SimpleGrid>
        </Paper>

        <Divider />
        <div>
          <Text fw={700} mb="sm">{t(isDual ? 'centrifuge.centrifuge1' : 'centrifuge.lowSpeed')}</Text>
          {operatingInputs(lowSpeed, setLowSpeed)}
        </div>
        <div>
          <Text fw={700} mb="sm">{t(isDual ? 'centrifuge.centrifuge2' : 'centrifuge.highSpeed')}</Text>
          {operatingInputs(highSpeed, setHighSpeed)}
        </div>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button color="green" onClick={submit} disabled={!formValid} loading={submitting}>
            {t('centrifuge.generate')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
